'use client'
import { useCallback, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { ISpotlightConfig } from '@/types/cinematics'
import { PHONE_CAPTURE_SURFACES } from '@/constants/profileCompleteness'
import { getLocalStorageData, setLocalStorageData } from '@/utils/handleLocalStorage'
import { getSessionStorageData, setSessionStorageData } from '@/utils/handleSessionStorage'
import { openExternal, resolveNavTarget } from '@/utils/url'
import { useProfileCompleteness } from '@/hooks/users/useProfileCompleteness'
import { useProfile } from '@/hooks/users/useProfile'
import { useTagManager } from '@/hooks/analytics/useTagManager'

const SEEN_VALUE = 'true'
const PHONE_MISSING = 'phone-missing'

/**
 * Every route change renders a fresh `Layout`, so an unanswered step is mounted
 * again on each page. Module scope is what survives those remounts and keeps one
 * prompt worth one view; a full reload starts a new session and may count again.
 * Keyed per profile so a second account on a shared device still reports its own.
 */
const reportedViews = new Set<string>()

/**
 * Steps answered in this browsing session only. A step whose insistence rule
 * lives on the server must not be written to localStorage: that would cap a
 * policy of three weekly attempts at one per browser, for everyone who takes
 * the action and then leaves without finishing.
 *
 * `sessionStorage` rather than module scope, because module scope dies with the
 * page: someone who pressed the action, landed on the form and then reloaded
 * was asked again immediately — and again on the next reload, since taking the
 * action is not a refusal and never spends one of the server's attempts.
 */
const answeredKey = (profileId: string, storageKey: string) =>
  `spotlight-answered:${profileId}:${storageKey}`

/**
 * Floor under the session ledger. The storage helpers swallow their throw and
 * report '' — Safari private mode, in-app WebViews, blocked site data — and an
 * unrecorded answer makes the step eligible again on the very next route, so it
 * reopened on top of the profile form the user had just been sent to. This
 * survives client-side navigation the way the storage read is supposed to; only
 * a reload defeats it, which is the part storage is there for.
 */
const answeredInMemory = new Set<string>()

/**
 * Permanent answers stay on the browser-global key so that turning this on does
 * not re-show a step to everyone who had already dismissed it, while new
 * answers are recorded per profile — a second account on a shared device gets
 * asked. The legacy key is read, never written, and decays on its own.
 */
const permanentKey = (profileId: string, storageKey: string) => `${storageKey}:${profileId}`

const isPermanentlyAnswered = (profileId: string, storageKey: string) =>
  getLocalStorageData(permanentKey(profileId, storageKey)) === SEEN_VALUE ||
  getLocalStorageData(storageKey) === SEEN_VALUE

export interface UseSpotlightQueueParams {
  spotlights: ISpotlightConfig[]
  /** The caller's gate: scene flag plus an identified, resolved session. */
  enabled: boolean
}

export interface UseSpotlightQueueResult {
  activeSpotlight?: ISpotlightConfig
  /** Another step follows, so the primary button reads "Next" instead of "Got it". */
  hasNextStep: boolean
  reportShown: () => void
  /** The step's target is not on this page, so the queue moves past it. */
  skipActive: () => void
  completeActive: () => void
  dismissActive: () => void
}

/**
 * Turns the configured spotlights into a one-at-a-time wizard. Array order is
 * step order, a step is skipped when its condition does not hold, and answering
 * one advances to the next.
 */
export const useSpotlightQueue = (params: UseSpotlightQueueParams): UseSpotlightQueueResult => {
  const { spotlights, enabled } = params
  const router = useRouter()
  const { sendGtm } = useTagManager()
  const { shouldPromptPhone, isPhoneMissing, profileId } = useProfileCompleteness()
  const { updateProfile } = useProfile()
  // Bumped when a step is answered so selection re-runs and the next one shows.
  const [answeredGeneration, setAnsweredGeneration] = useState(0)
  // Steps whose target is not on this page. Held per mount, not persisted: the
  // element may well exist on the next route.
  const [unavailableKeys, setUnavailableKeys] = useState<string[]>([])

  const isEligible = useCallback((spotlight: ISpotlightConfig) => {
    // CMS data has no runtime schema validation, and a step with no storage key
    // would reappear on every page load.
    if (!spotlight?.storageKey) return false
    const sessionKey = answeredKey(profileId, spotlight.storageKey)
    if (answeredInMemory.has(sessionKey)) return false
    if (getSessionStorageData(sessionKey) === SEEN_VALUE) return false
    if (unavailableKeys.includes(spotlight.storageKey)) return false
    if (isPermanentlyAnswered(profileId, spotlight.storageKey)) return false
    // A declared condition gates; an unrecognised one fails CLOSED. The
    // cinematic has no runtime validation, so `phone_missing` instead of
    // `phone-missing` used to mean "always eligible" — the step shown to
    // everyone, including people who already gave us their number, and then
    // recorded as permanently answered. A typo costing a step nobody sees is
    // recoverable; one that prompts the whole base is not.
    if (spotlight.condition) {
      // Both halves, as the schema and docs state. `promptPhone` is expected to
      // imply `phoneMissing`, but it is the server's pacing flag and this is the
      // step's own subject — if the two ever disagree, asking someone who
      // already gave us their number is the worse failure.
      return spotlight.condition === PHONE_MISSING && isPhoneMissing && shouldPromptPhone
    }
    return true
  }, [shouldPromptPhone, isPhoneMissing, unavailableKeys, profileId])

  const queue = useMemo(
    () => (enabled ? spotlights.filter(isEligible) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- answeredGeneration re-runs selection once a step is answered
    [spotlights, enabled, isEligible, answeredGeneration],
  )

  const activeSpotlight = queue[0]
  const hasNextStep = queue.length > 1
  const isPhoneStep = activeSpotlight?.condition === PHONE_MISSING

  const markAnswered = useCallback((spotlight: ISpotlightConfig) => {
    // Every answer lands in memory first, whichever store it belongs in. Both
    // helpers swallow their throw and report nothing, so with storage blocked an
    // answered step stayed eligible, sat at the head of the queue, and the steps
    // behind it — the phone prompt, the point of this feature — never came up.
    answeredInMemory.add(answeredKey(profileId, spotlight.storageKey))

    // A step the server decides on is only suppressed for this session; writing
    // the permanent flag would override the policy that owns it.
    if (spotlight.condition === PHONE_MISSING) {
      setSessionStorageData(answeredKey(profileId, spotlight.storageKey), SEEN_VALUE)
    } else {
      setLocalStorageData(permanentKey(profileId, spotlight.storageKey), SEEN_VALUE)
    }
    setAnsweredGeneration(generation => generation + 1)
  }, [profileId])

  const skipActive = useCallback(() => {
    if (!activeSpotlight) return
    // Nothing is recorded as answered: a step whose target is missing here has
    // not been shown, and must not block the steps behind it. Idempotent because
    // Spotlight re-measures whenever its callback identities change, and a
    // repeated entry would only lengthen the scan that reads it.
    setUnavailableKeys(keys =>
      keys.includes(activeSpotlight.storageKey) ? keys : keys.concat(activeSpotlight.storageKey)
    )
  }, [activeSpotlight])

  const reportShown = useCallback(() => {
    if (!isPhoneStep || !activeSpotlight) return
    // Scoped to the profile: a second account on a shared device is a second
    // prompt, and would otherwise be swallowed by the first one's entry.
    const viewKey = `${profileId}:${activeSpotlight.storageKey}`
    if (reportedViews.has(viewKey)) return
    reportedViews.add(viewKey)
    sendGtm.USER_PHONE_PROMPT_VIEWED({ type: PHONE_CAPTURE_SURFACES.spotlight })
  }, [isPhoneStep, activeSpotlight, profileId, sendGtm])

  const completeActive = useCallback(() => {
    if (!activeSpotlight) return
    markAnswered(activeSpotlight)

    // Taking the action is not a refusal, so it must not spend one of the
    // server-side attempts the way "not now" does.
    const target = resolveNavTarget(activeSpotlight.action?.url)
    if (!target) return
    if (target.external) {
      openExternal(target.url)
      return
    }
    router.push(target.url)
  }, [activeSpotlight, markAnswered, router])

  const dismissActive = useCallback(() => {
    if (!activeSpotlight) return
    markAnswered(activeSpotlight)
    if (activeSpotlight.condition !== PHONE_MISSING) return

    // The insistence rule lives on the server (PRO-896), so a refusal has to
    // reach it; the local flag only stops the step flashing again before the
    // profile query is invalidated.
    updateProfile({ phonePromptDismissed: true })
    sendGtm.USER_PHONE_PROMPT_DISMISSED({ type: PHONE_CAPTURE_SURFACES.spotlight })
  }, [activeSpotlight, markAnswered, updateProfile, sendGtm])

  return { activeSpotlight, hasNextStep, reportShown, skipActive, completeActive, dismissActive }
}
